// "Meta charges" on a client-funded invoice: what Meta actually charged the
// client's own card on their ad account during the invoice period. Synced
// nightly from the ad account's activity log; the same rows are attached to
// the invoice email as a CSV. Used on the client and staff invoice pages.
import React from 'react';

import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const money = (value, currency = 'USD') => {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value ?? '—');
  return n.toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2 });
};

const csvCell = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function MetaChargesCard({ metaCharges, invoiceNumber, elevation = 3 }) {
  if (!metaCharges) return null;
  const charges = metaCharges.charges || [];
  const multiAccount = new Set(charges.map((c) => c.ad_account_id)).size > 1;
  // Columns before the last (amount) column, for the summary rows' label cell.
  const labelSpan = 2 + (multiAccount ? 1 : 0) + (metaCharges.estimated ? 1 : 0);

  const estimated = Boolean(metaCharges.estimated);

  const download = () => {
    const header = ['Date', estimated ? 'Meta charge (whole account)' : 'Amount', 'Currency', 'Meta transaction ID', 'Ad account', 'Ad account ID'];
    if (estimated) header.push('Your share %', 'Your share (estimated)');
    const rows = [
      header,
      ...charges.map((c) => {
        const line = [c.date, c.amount, c.currency, c.transaction_id, c.ad_account_name, c.ad_account_id];
        if (estimated) line.push(c.share_pct ?? '', c.your_share);
        return line;
      }),
      [],
      ...(estimated
        ? [
            ['Total Meta charges (whole account)', metaCharges.account_total],
            ['Your share of Meta charges (estimated)', metaCharges.total],
          ]
        : [['Total charged by Meta', metaCharges.total]]),
      ['Facebook media spend this period', metaCharges.media_spend],
      ['Difference', metaCharges.difference],
    ];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `${invoiceNumber || 'invoice'}-meta-charges.csv`;
    a.click();
    window.URL.revokeObjectURL(a.href);
  };

  const summaryRow = (label, value, bold = false) => (
    <TableRow key={label}>
      <TableCell sx={{ fontWeight: bold ? 700 : 400, borderBottom: 'none' }} colSpan={labelSpan}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400, borderBottom: 'none' }}>
        {money(value)}
      </TableCell>
    </TableRow>
  );

  return (
    <Card elevation={elevation} sx={{ borderRadius: 3 }}>
      <CardHeader
        title="Meta ad charges"
        subheader="Charged by Meta directly to the payment method on your ad account"
        sx={{ pb: 0 }}
        action={
          charges.length ? (
            <Button
              size="small"
              variant="outlined"
              onClick={download}
              sx={{ borderRadius: 9999, textTransform: 'none' }}
            >
              Download CSV
            </Button>
          ) : null
        }
      />
      <CardContent>
        {metaCharges.complete === false ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Incomplete list.</strong> {metaCharges.incomplete_note}
          </Alert>
        ) : null}
        {charges.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                {multiAccount && <TableCell>Ad account</TableCell>}
                <TableCell>Meta transaction ID</TableCell>
                {estimated && <TableCell align="right">Meta charge (whole account)</TableCell>}
                <TableCell align="right">{estimated ? 'Your share (est.)' : 'Amount'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {charges.map((c) => (
                <TableRow key={c.transaction_id}>
                  <TableCell>
                    {new Date(`${c.date}T00:00:00`).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </TableCell>
                  {multiAccount && <TableCell>{c.ad_account_name || c.ad_account_id}</TableCell>}
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{c.transaction_id}</TableCell>
                  {estimated && <TableCell align="right">{money(c.amount, c.currency)}</TableCell>}
                  <TableCell align="right">
                    {money(c.your_share ?? c.amount, c.currency)}
                    {estimated && c.share_pct !== null && c.share_pct !== undefined ? (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}({c.share_pct}%)
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {summaryRow(
                estimated ? 'Your share of Meta charges (estimated)' : 'Total charged by Meta',
                metaCharges.total,
                true,
              )}
              {metaCharges.media_spend !== undefined && summaryRow('Facebook media spend this period', metaCharges.media_spend)}
              {metaCharges.difference !== undefined && summaryRow('Difference (timing of Meta\u2019s billing)', metaCharges.difference)}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Meta made no charges to your ad account during this invoice period.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
          {metaCharges.note}
        </Typography>
        {metaCharges.split_note ? (
          <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
            <strong>Estimated split.</strong> {metaCharges.split_note}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
